// Room page: host starts the game (command, analytics, speech-complete → transition)

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from './useAuth';
import { useRoomMediaElements } from './useRoomMediaElements';
import { useRoomSocket } from './useRoomSocket';
import { analyticsService } from '../services/analyticsService';
import {
  generateCommandId,
  createLogger,
} from '@airhead-games/shared/utilities';
import {
  RoomCommand,
  SenderRoleType,
  RoomActionType,
  MediaActionType,
  RoomUser,
} from '@airhead-games/shared/types';
import { gameLibrary } from '@airhead-games/shared/configs';

const logger = createLogger('useRoomGameStart');

interface UseRoomGameStartArgs {
  roomCode: string | undefined;
  host: RoomUser | undefined;
  gameId: string | undefined;
  gameName: string | undefined;
  playerCount: number;
  onTransition: () => Promise<void> | void;
}

interface UseRoomGameStartResult {
  startGame: () => Promise<void>;
  gameStarting: boolean;
  startGameError: string | null;
}

export function useRoomGameStart({
  roomCode,
  host,
  gameId,
  gameName,
  playerCount,
  onTransition,
}: UseRoomGameStartArgs): UseRoomGameStartResult {
  const { sendCommand } = useRoomSocket();
  const { refetchUser } = useAuth();
  const { getMediaElements } = useRoomMediaElements();
  const [gameStarting, setGameStarting] = useState(false);
  const [startGameError, setStartGameError] = useState<string | null>(null);

  const loonCost =
    gameLibrary.find((g) => g.gameCore.gameId === gameId)?.gameCore.loonCost ??
    0;

  const gameStartedSpeechCompleted =
    getMediaElements({ content: 'room.startGame' })[0]?.mediaAction ===
    MediaActionType.COMPLETED;

  const startGame = useCallback(async () => {
    if (!roomCode || !host) {
      logger.error('Missing roomCode or host info');
      return;
    }

    if (loonCost > 0) {
      const freshUser = await refetchUser();
      const freshBalance = freshUser?.loonBalance ?? 0;
      if (freshBalance < loonCost) {
        setStartGameError('Go back to get more Loons');
        return;
      }
    }
    setStartGameError(null);

    logger.info('Starting game...');
    setGameStarting(true);

    try {
      analyticsService.trackButtonClick('Start Game', {
        room_code: roomCode,
        game_name: gameName || 'Unknown Game',
        player_count: playerCount,
      });

      analyticsService.trackGameStarted({
        game_name: gameName || 'Unknown Game',
        player_count: playerCount,
        room_code: roomCode,
      });

      const command: RoomCommand = {
        id: generateCommandId(),
        type: 'RoomAction',
        action: RoomActionType.START,
        senderRole: SenderRoleType.HOST,
        clientTimestamp: Date.now(),
        roomCode,
        host,
      };
      sendCommand(command);
      logger.debug('Game start command sent');
    } catch (error) {
      logger.error('Error sending start game command:', error);
      setGameStarting(false);
    }
  }, [
    gameName,
    host,
    loonCost,
    playerCount,
    refetchUser,
    roomCode,
    sendCommand,
  ]);

  useEffect(() => {
    if (!gameStarting || !gameStartedSpeechCompleted) {
      return;
    }

    logger.info('Starting room transition to game...');
    void (async () => {
      try {
        await Promise.resolve(onTransition());
      } catch (error) {
        logger.error('Error during room game start transition:', error);
      } finally {
        setGameStarting(false);
      }
    })();
  }, [gameStarting, gameStartedSpeechCompleted, onTransition]);

  return { startGame, gameStarting, startGameError };
}
